import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertTriangle, CheckCircle, Search, XCircle } from 'lucide-react';
import { fadeInUp } from '../../lib/animations';

// ─── Lógica de negócio ──────────────────────────────────────────────────────

interface DotResult {
  weekNumber: number;
  year: number;
  manufactureDate: Date;
  recommendedReplace: Date;
  mandatoryDiscard: Date;
  yearsOld: number;
  status: 'ok' | 'warning' | 'danger';
}

/** Converte os 4 últimos dígitos do DOT em dados de validade */
function parseDot(raw: string): DotResult | null {
  const code = raw.trim().replace(/\D/g, '');
  if (code.length !== 4) return null;

  const weekNumber = parseInt(code.slice(0, 2), 10);
  const yearShort = parseInt(code.slice(2, 4), 10);

  if (weekNumber < 1 || weekNumber > 53) return null;

  // Formato DOT de 4 dígitos foi adotado a partir de 2000
  const year = 2000 + yearShort;

  // Data aproximada: primeira segunda-feira da semana informada
  const jan1 = new Date(Date.UTC(year, 0, 1));
  const dayOfWeek = jan1.getUTCDay(); // 0 = Domingo
  const offsetToMonday = dayOfWeek === 0 ? 1 : 8 - dayOfWeek;
  const manufactureDate = new Date(
    Date.UTC(year, 0, offsetToMonday + (weekNumber - 1) * 7),
  );

  const recommendedReplace = new Date(manufactureDate);
  recommendedReplace.setUTCFullYear(recommendedReplace.getUTCFullYear() + 5);

  const mandatoryDiscard = new Date(manufactureDate);
  mandatoryDiscard.setUTCFullYear(mandatoryDiscard.getUTCFullYear() + 10);

  const yearsOld =
    (Date.now() - manufactureDate.getTime()) / (1000 * 60 * 60 * 24 * 365.25);

  let status: DotResult['status'] = 'ok';
  if (yearsOld >= 10) status = 'danger';
  else if (yearsOld >= 5) status = 'warning';

  return {
    weekNumber,
    year,
    manufactureDate,
    recommendedReplace,
    mandatoryDiscard,
    yearsOld,
    status,
  };
}

function fmtDate(d: Date): string {
  return d.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
}

// ─── Componente ─────────────────────────────────────────────────────────────

interface Props {
  className?: string;
}

const statusMap = {
  ok: {
    Icon: CheckCircle,
    color: 'text-emerald-400',
    border: 'border-emerald-400/30 bg-emerald-400/10',
    label: 'Pneu dentro do prazo',
    tip: '✅ Seu pneu ainda está dentro do prazo recomendado. Verifique a pressão regularmente e inspecione visualmente o desgaste.',
    tipClass: 'text-emerald-300 bg-emerald-400/10 border-emerald-400/20',
  },
  warning: {
    Icon: AlertTriangle,
    color: 'text-amber-400',
    border: 'border-amber-400/30 bg-amber-400/10',
    label: 'Troca recomendada',
    tip: '⚠️ Seu pneu ultrapassou 5 anos. Mesmo visualmente sem defeitos, a borracha pode estar ressecada internamente. Leve a um borracheiro para avaliação antes da próxima viagem longa.',
    tipClass: 'text-amber-300 bg-amber-400/10 border-amber-400/20',
  },
  danger: {
    Icon: XCircle,
    color: 'text-red-400',
    border: 'border-red-400/30 bg-red-400/10',
    label: 'Descarte obrigatório',
    tip: '🚨 Pneu com mais de 10 anos. Substitua imediatamente — o risco de estouro e perda de controle do veículo é alto independente da aparência.',
    tipClass: 'text-red-300 bg-red-400/10 border-red-400/20',
  },
} as const;

/**
 * Calculadora de Validade de Pneu pelo código DOT.
 * Componente independente — pode ser embutido em qualquer página ou post de blog.
 */
const CalculadoraDOT: React.FC<Props> = ({ className = '' }) => {
  const [dotCode, setDotCode] = useState('');
  const [result, setResult] = useState<DotResult | null>(null);
  const [error, setError] = useState('');

  const handleVerify = () => {
    const parsed = parseDot(dotCode);
    if (!parsed) {
      setError(
        'Código inválido. Informe apenas os 4 últimos dígitos do DOT (ex: 1423).',
      );
      setResult(null);
      return;
    }
    setError('');
    setResult(parsed);
  };

  return (
    <motion.div
      variants={fadeInUp}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.2 }}
      className={`rounded-2xl bg-slate-900 border border-slate-800 p-6 sm:p-8 ${className}`}
    >
      {/* Input */}
      <div className="space-y-5">
        <div>
          <label className="block text-sm font-medium text-slate-300 mb-1.5">
            Últimos 4 dígitos do código DOT
          </label>
          <div className="flex gap-3">
            <input
              type="text"
              inputMode="numeric"
              maxLength={4}
              placeholder="Ex: 1423"
              value={dotCode}
              onChange={e =>
                setDotCode(e.target.value.replace(/\D/g, '').slice(0, 4))
              }
              onKeyDown={e => e.key === 'Enter' && handleVerify()}
              className="flex-1 rounded-xl bg-slate-800 border border-slate-700 px-4 py-3 text-white placeholder-slate-500 font-mono text-xl tracking-widest focus:border-amber-400 focus:outline-none focus:ring-1 focus:ring-amber-400"
            />
            <button
              onClick={handleVerify}
              className="flex items-center gap-2 px-5 py-3 rounded-xl bg-amber-400 text-gray-900 font-semibold hover:bg-amber-300 active:scale-95 transition-all"
            >
              <Search className="h-4 w-4" strokeWidth={2} />
              Verificar
            </button>
          </div>
          {error && <p className="mt-2 text-sm text-red-400">{error}</p>}
          <p className="mt-2 text-xs text-slate-500">
            Os 4 últimos dígitos estão gravados na lateral do pneu, após as letras
            "DOT". Exemplo: DOT U2LL <strong className="text-slate-400">LMLR 1423</strong>.
          </p>
        </div>

        {/* Resultado */}
        <AnimatePresence mode="wait">
          {result && (() => {
            const cfg = statusMap[result.status];
            const { Icon } = cfg;
            return (
              <motion.div
                key={result.year + result.weekNumber}
                variants={fadeInUp}
                initial="hidden"
                animate="visible"
                exit={{ opacity: 0 }}
                className="space-y-3"
              >
                {/* Banner de status */}
                <div
                  className={`flex items-center gap-3 rounded-xl border p-4 ${cfg.border}`}
                >
                  <Icon
                    className={`h-6 w-6 shrink-0 ${cfg.color}`}
                    strokeWidth={2}
                  />
                  <div>
                    <p className={`font-semibold ${cfg.color}`}>{cfg.label}</p>
                    <p className="text-xs text-slate-400 mt-0.5">
                      Fabricado na {result.weekNumber}ª semana de {result.year}{' '}
                      · {result.yearsOld.toFixed(1)} anos de uso
                    </p>
                  </div>
                </div>

                {/* Grid de datas */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {[
                    {
                      label: 'Data de Fabricação',
                      value: fmtDate(result.manufactureDate),
                      sub: `Semana ${result.weekNumber}/${result.year}`,
                    },
                    {
                      label: 'Troca Recomendada',
                      value: fmtDate(result.recommendedReplace),
                      sub: '5 anos após fabricação',
                    },
                    {
                      label: 'Descarte Obrigatório',
                      value: fmtDate(result.mandatoryDiscard),
                      sub: '10 anos após fabricação',
                    },
                  ].map(item => (
                    <div
                      key={item.label}
                      className="rounded-xl bg-slate-800 p-4 text-center"
                    >
                      <p className="text-xs font-medium text-slate-400 uppercase tracking-wide mb-1">
                        {item.label}
                      </p>
                      <p className="text-base font-bold text-white leading-tight">
                        {item.value}
                      </p>
                      <p className="text-xs text-slate-500 mt-1">{item.sub}</p>
                    </div>
                  ))}
                </div>

                {/* Dica contextual */}
                <p
                  className={`text-sm rounded-xl border px-4 py-3 leading-relaxed ${cfg.tipClass}`}
                >
                  {cfg.tip}
                </p>
              </motion.div>
            );
          })()}
        </AnimatePresence>
      </div>
    </motion.div>
  );
};

export default CalculadoraDOT;

